import { useTranslation } from "react-i18next";
import {
	FrontendCompany,
	FrontendUserInfo,
	FrontendSampleInstance,
	FrontendOrganizationLicense,
	FrontendClient,
	FrontendOrganization,
	FrontendOrder,
	FrontendCompanyTestCategory,
	FrontendCompanyWithAdditionalInfomation,
	FrontendSampleSpecificationDirectory,
	FrontendSampleSpecification,
} from "node-library/dist/portal";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import { GreenButton, TextButton } from "../../../common-components/button";
import { Card, Divider } from "@mui/material";
import {
	BillingIcon,
	CommentIcon,
	DeleteIcon,
	OrderIcon,
	UserIcon,
} from "../../../common-components/svgIcons";
import { NeutralColor, PrimaryColor } from "node-library/dist/colors";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";

import { SetStateAction, useState } from "react";
import { useSnackbar } from "../../../common-components/toast/showToastMessage";
import { Severity } from "node-library/dist/constants";
import { YellowWarnIcon } from "../../../common-components/toast/warnIcons";
import {
	CommonSwitch,
	CustomTextInput,
} from "../../../common-components/inputs";
import SamplePreviewTable from "./samplePreviewTable";
import { validateEmail } from "../../../helpers/inputHelper";
import { CollapsibleCard } from "../../../common-components/card";
import CopyEmailSection from "../common/copyEmailSection";
import { ButtonTextLoadingAnimation } from "../../../common-components/animation/ButtonLoadingAnimation";

interface Props {
	company: FrontendCompanyWithAdditionalInfomation;
	setCompany: React.Dispatch<
		React.SetStateAction<FrontendCompanyWithAdditionalInfomation | undefined>
	>;
	user: FrontendUserInfo;

	isEditOrder?: boolean;
	order: FrontendOrder | undefined;
	selectedClient: FrontendClient | undefined;
	selectedOrganization: FrontendOrganization | undefined;
	orderTestCategory: FrontendCompanyTestCategory;

	sampleInstances: Array<FrontendSampleInstance>;
	userLicenses: Array<FrontendOrganizationLicense>;
	licenseId: string;
	testReportEmails: Array<string>;
	billingInfoEmails: Array<string>;
	setTestReportEmails: React.Dispatch<SetStateAction<Array<string>>>;
	setBillingInfoEmails: React.Dispatch<SetStateAction<Array<string>>>;

	sameBillingInfoAsTestInfo: boolean;
	setSameBillingInfoAsTestInfo: React.Dispatch<SetStateAction<boolean>>;

	billingName: string;
	setBillingName: React.Dispatch<SetStateAction<string>>;
	billingAddress: string;
	setBillingAddress: React.Dispatch<SetStateAction<string>>;
	city: string;
	setCity: React.Dispatch<SetStateAction<string>>;
	postalCode: string;
	setPostalCode: React.Dispatch<SetStateAction<string>>;
	province: string;
	setProvince: React.Dispatch<SetStateAction<string>>;
	country: string;
	setCountry: React.Dispatch<SetStateAction<string>>;
	phoneNumber: string;
	setPhoneNumber: React.Dispatch<SetStateAction<string>>;
	billingEmail: string;
	setBillingEmail: React.Dispatch<SetStateAction<string>>;

	orderHasComment: boolean;
	setOrderHasComment: React.Dispatch<SetStateAction<boolean>>;
	orderComment: string;
	setOrderComment: React.Dispatch<SetStateAction<string>>;
	goBackToOrderPage: () => void;
	submitOrder: () => void;
	submittingOrder: boolean;
	specDirectories: Array<FrontendSampleSpecificationDirectory>;
	setSampleSpecifications: React.Dispatch<
		React.SetStateAction<FrontendSampleSpecification[]>
	>;
}

export default function OrderSubmissionConfirm(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const {
		company,
		setCompany,
		user,
		orderTestCategory,
		selectedClient,
		selectedOrganization,
		sampleInstances,
		userLicenses,
		licenseId,
		testReportEmails,
		billingInfoEmails,
		setBillingInfoEmails,
		setTestReportEmails,
		sameBillingInfoAsTestInfo,
		setSameBillingInfoAsTestInfo,

		billingName,
		setBillingName,
		billingAddress,
		setBillingAddress,
		city,
		setCity,
		postalCode,
		setPostalCode,
		province,
		setProvince,
		country,
		setCountry,
		phoneNumber,
		setPhoneNumber,
		billingEmail,
		setBillingEmail,
		goBackToOrderPage,
		orderHasComment,
		setOrderHasComment,
		orderComment,
		setOrderComment,
		submitOrder,
		submittingOrder,
		specDirectories,
		setSampleSpecifications,
	} = props;

	const [testReportEmailsErrorMsgs, setTestReportEmailsErrorMsgs] = useState<
		Array<string>
	>([]);
	const [testReportInfoCardExpanded, setTestReportInfoCardExpanded] =
		useState<boolean>(true);

	const [billingInfoEmailsErrorMsgs, setBillingInfoEmailsErrorMsgs] = useState<
		Array<string>
	>([]);
	const [billingInfoInfoCardExpanded, setBillingInfoInfoCardExpanded] =
		useState<boolean>(true);

	const [sampleInfoCardExpanded, setSampleInfoCardExpanded] =
		useState<boolean>(true);

	//billing validations
	const [billingNameErrorMsg, setBillingNameErrorMsg] = useState<string>("");
	const [billingAddressErrorMsg, setBillingAddressErrorMsg] =
		useState<string>("");
	const [cityErrorMsg, setCityErrorMsg] = useState<string>("");
	const [postalCodeErrorMsg, setPostalCodeErrorMsg] = useState<string>("");
	const [provinceErrorMsg, setProvinceErrorMsg] = useState<string>("");
	const [countryErrorMsg, setCountryErrorMsg] = useState<string>("");
	const [phoneNumberErrorMsg, setPhoneNumberErrorMsg] = useState<string>("");
	const [billingEmailErrorMsg, setBillingEmailErrorMsg] = useState<string>("");

	const [orderCommentErrorMsg, setOrderCommentErrorMsg] = useState<string>("");

	const mandatoryFields: Array<any> = [
		{
			id: "billing-name-input",
			label: translation("PersonalOrCompanyName"),
			setValue: (value: string) => {
				setBillingName(value);
			},
			field: billingName,
			predicates: [billingName && billingName.length > 0],

			errorMsgs: [translation("MissingInformation")],
			errorMsg: billingNameErrorMsg,
			setError: setBillingNameErrorMsg,
		},
		{
			id: "billing-address-input",
			label: translation("PersonalOrCompanyAddress"),
			setValue: (value: string) => {
				setBillingAddress(value);
			},
			field: billingAddress,
			predicates: [billingAddress && billingAddress.length > 0],
			errorMsgs: [translation("MissingInformation")],
			errorMsg: billingAddressErrorMsg,
			setError: setBillingAddressErrorMsg,
		},
		{
			id: "billing-city-input",
			label: translation("City"),
			setValue: (value: string) => {
				setCity(value);
			},
			field: city,
			predicates: [city && city.length > 0],
			errorMsgs: [translation("MissingInformation")],
			errorMsg: cityErrorMsg,

			setError: setCityErrorMsg,
		},
		{
			id: "billing-postal-code-input",
			label: translation("PostalCode"),
			setValue: (value: string) => {
				setPostalCode(value);
			},
			field: postalCode,
			predicates: [postalCode && postalCode.length > 0],
			errorMsgs: [translation("MissingInformation")],
			errorMsg: postalCodeErrorMsg,

			setError: setPostalCodeErrorMsg,
		},
		{
			id: "billing-province-input",
			label: translation("Province"),
			setValue: (value: string) => {
				setProvince(value);
			},
			field: province,
			predicates: [province && province.length > 0],
			errorMsgs: [translation("MissingInformation")],
			errorMsg: provinceErrorMsg,

			setError: setProvinceErrorMsg,
		},
		{
			id: "billing-country-input",
			label: translation("Country"),
			setValue: (value: string) => {
				setCountry(value);
			},
			field: country,
			predicates: [country && country.length > 0],
			errorMsgs: [translation("MissingInformation")],
			errorMsg: countryErrorMsg,

			setError: setCountryErrorMsg,
		},
		{
			id: "billing-phone-number-input",
			label: translation("PhoneNumber"),
			setValue: (value: string) => {
				setPhoneNumber(value);
			},
			field: phoneNumber,
			predicates: [phoneNumber && phoneNumber.length > 0],
			errorMsgs: [translation("MissingInformation")],
			errorMsg: phoneNumberErrorMsg,

			setError: setPhoneNumberErrorMsg,
		},
		{
			id: "billing-email-input",
			label: translation("Email"),
			setValue: (value: string) => {
				setBillingEmail(value);
			},
			field: billingEmail,
			predicates: [
				billingEmail && billingEmail.length > 0,
				validateEmail(billingEmail),
			],
			errorMsgs: [
				translation("MissingInformation"),
				translation("InvalidEmail"),
			],
			errorMsg: billingEmailErrorMsg,

			setError: setBillingEmailErrorMsg,
		},
	];

	function newTestReportEmail() {
		if (testReportEmails?.length >= 5) {
			openSnackbar(
				translation("LimitReached"),
				Severity.WARN,
				translation("EmailLimitReachedDescription"),
				<YellowWarnIcon />,
			);
			return;
		}
		setTestReportEmails((prevItems) => [...prevItems, ""]);
		setTestReportEmailsErrorMsgs((prevItems) => [...prevItems, ""]);
	}
	function removeTestReportEmail(index: number) {
		if (index >= 0 && index < testReportEmails.length) {
			setTestReportEmails((prevItems) =>
				prevItems.filter((_, i) => i !== index),
			);
			setTestReportEmailsErrorMsgs((prevItems) =>
				prevItems.filter((_, i) => i !== index),
			);
		} else {
			return;
		}
	}
	function updateTestReportEmail(index: number, newItem: string) {
		// Create a new array with the updated item at the specific index
		const updatedEmails = testReportEmails.map(
			(email, i) => (i === index ? newItem : email), // If the index matches, replace the item
		);

		// Update the state with the new array
		setTestReportEmails(updatedEmails);
	}

	function newBillingInfoEmail() {
		if (billingInfoEmails?.length >= 5) {
			openSnackbar(
				translation("LimitReached"),
				Severity.WARN,
				translation("EmailLimitReachedDescription"),
				<YellowWarnIcon />,
			);
			return;
		}
		setBillingInfoEmails((prevItems) => [...prevItems, ""]);
		setBillingInfoEmailsErrorMsgs((prevItems) => [...prevItems, ""]);
	}
	function removeBillingInfoEmail(index: number) {
		if (index >= 0 && index < testReportEmails.length) {
			setBillingInfoEmails((prevItems) =>
				prevItems.filter((_, i) => i !== index),
			);
			setBillingInfoEmailsErrorMsgs((prevItems) =>
				prevItems.filter((_, i) => i !== index),
			);
		} else {
			return;
		}
	}
	function updateBillingInfoEmail(index: number, newItem: string) {
		// Create a new array with the updated item at the specific index
		const updatedEmails = billingInfoEmails.map(
			(email, i) => (i === index ? newItem : email), // If the index matches, replace the item
		);

		// Update the state with the new array
		setBillingInfoEmails(updatedEmails);
	}

	function proceedToSubmitOrder() {
		//validate the emails
		//clear error msgs
		const resetArray = testReportEmailsErrorMsgs.map(() => "");

		const billingResetArray = billingInfoEmailsErrorMsgs.map(() => "");

		let hasError: boolean = false;
		for (let i = 0; i < testReportEmails.length; i++) {
			if (!validateEmail(testReportEmails[i])) {
				resetArray[i] = translation("InvalidEmail");
				hasError = true;
			}
		}
		setTestReportEmailsErrorMsgs(resetArray);

		for (let i = 0; i < billingInfoEmails.length; i++) {
			if (!validateEmail(billingInfoEmails[i])) {
				billingResetArray[i] = translation("InvalidEmail");
				hasError = true;
			}
		}
		setBillingInfoEmailsErrorMsgs(billingResetArray);

		//validate mandatory fields
		if (!sameBillingInfoAsTestInfo) {
			for (const field of mandatoryFields) {
				field.setError("");
				for (let i = 0; i < field.predicates.length; i++) {
					if (!field.predicates[i]) {
						hasError = true;
						field.setError(field.errorMsgs[i]);
						break;
					}
				}
			}
		}
		if (orderHasComment && (!orderComment || orderComment.trim().length <= 0)) {
			setOrderCommentErrorMsg(translation("MissingInformation"));
			hasError = true;
		}

		if (hasError) {
			return;
		}
		submitOrder();
	}

	const testReportEmailItems: Array<React.ReactNode> = testReportEmails.map(
		(email, index) => {
			return (
				<div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
					<div style={{ width: "100%" }}>
						<CustomTextInput
							id={`test-email-input-${index}`}
							key={index}
							value={email}
							setValue={(value) => {
								updateTestReportEmail(index, value);
							}}
							placeholder={translation("EmailAddress")}
							required
							errorMsg={testReportEmailsErrorMsgs[index]}
							setErrorMsg={(msg: string) => {
								const updatedMsgs = testReportEmailsErrorMsgs.map(
									(txt, i) => (i === index ? msg : txt), // If the index matches, replace the item
								);

								// Update the state with the new array
								setTestReportEmailsErrorMsgs(updatedMsgs);
							}}
						/>
					</div>
					<div
						style={{ cursor: "pointer" }}
						onClick={() => {
							removeTestReportEmail(index);
						}}
					>
						<DeleteIcon></DeleteIcon>
					</div>
				</div>
			);
		},
	);

	const billingInfoEmailItems: Array<React.ReactNode> = billingInfoEmails.map(
		(email, index) => {
			return (
				<div style={{ display: "flex", gap: "10px", alignItems: "center" }}>
					<div style={{ width: "100%" }}>
						<CustomTextInput
							id={`billing-email-input-${index}`}
							key={index}
							value={email}
							setValue={(value) => {
								updateBillingInfoEmail(index, value);
							}}
							placeholder={translation("EmailAddress")}
							required
							errorMsg={billingInfoEmailsErrorMsgs[index]}
							setErrorMsg={(msg: string) => {
								const updatedMsgs = billingInfoEmailsErrorMsgs.map(
									(txt, i) => (i === index ? msg : txt), // If the index matches, replace the item
								);

								// Update the state with the new array
								setBillingInfoEmailsErrorMsgs(updatedMsgs);
							}}
						/>
					</div>
					<div
						style={{ cursor: "pointer" }}
						onClick={() => {
							removeBillingInfoEmail(index);
						}}
					>
						<DeleteIcon></DeleteIcon>
					</div>
				</div>
			);
		},
	);

	function updateSpecification(specification: FrontendSampleSpecification) {
		setSampleSpecifications((prevSpecs) => {
			// ADD 'return' here
			return prevSpecs.map((spec) => {
				if (spec.specificationId === specification.specificationId) {
					return specification;
				}
				return spec;
			});
		});
	}
	let selectedLicenseNumber = translation("NoLicense");
	if (licenseId) {
		selectedLicenseNumber =
			`${translation("License")} ${
				userLicenses.find((license) => {
					return license.licenseId === licenseId;
				})?.licenseNumber
			}` || translation("NoLicense");
	}
	const headerRow: React.ReactNode = (
		<div
			style={{
				display: "flex",
				justifyContent: "space-between",
				alignItems: "center",
				gap: "10px",
				flexWrap: "wrap",
			}}
		>
			<div
				style={{
					maxWidth: "60%",
					minWidth: "240px",
				}}
			>
				<div style={{ display: "flex", gap: "5px", alignItems: "center" }}>
					<div
						style={{ display: "flex", cursor: "pointer" }}
						onClick={() => goBackToOrderPage()}
					>
						<KeyboardArrowLeftIcon />
					</div>
					<div className="typography-heading-01">
						{translation("OrderReview")}
					</div>
				</div>

				<div className="overflow-ellipsis-text" style={{ marginLeft: "33px" }}>
					<div className="typography-body-01 overflow-ellipsis-text">
						{selectedLicenseNumber}
					</div>
				</div>
			</div>

			<GreenButton
				id={"submit-order-button"}
				onClick={() => {
					proceedToSubmitOrder();
				}}
				disabled={sampleInstances.length === 0 || submittingOrder}
			>
				{submittingOrder ? (
					<ButtonTextLoadingAnimation
						text={translation("Processing")}
					></ButtonTextLoadingAnimation>
				) : props.isEditOrder ? (
					translation("Update")
				) : (
					translation("SubmitOrder")
				)}
			</GreenButton>
		</div>
	);

	const billingInputs: Array<React.ReactNode> = mandatoryFields.map((field) => {
		return (
			<CustomTextInput
				id={field.id}
				label={field.label}
				value={field.field}
				setValue={field.setValue}
				required
				errorMsg={field.errorMsg}
				setErrorMsg={field.setError}
			/>
		);
	});

	const orderCommentsComponent: React.ReactNode = (
		<Card className="card-small-padding" style={{ padding: "16px 26px" }}>
			<div className="common-card-header">
				<div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
					<CommentIcon />

					<div className="typography-heading-03">
						{translation("OrderComments")}
					</div>
				</div>

				<CommonSwitch
					id="order-comment-switch"
					checked={orderHasComment}
					setChecked={setOrderHasComment}
				/>
			</div>

			{orderHasComment && (
				<div style={{ marginTop: "10px" }}>
					<CustomTextInput
						id="orderCommentTextarea"
						value={orderComment}
						setValue={(value) => {
							setOrderComment(value);
						}}
						placeholder={translation("OrderCommentsPlaceholder")}
						required
						isTextArea
						errorMsg={orderCommentErrorMsg}
						setErrorMsg={setOrderCommentErrorMsg}
					/>
				</div>
			)}
		</Card>
	);

	const userInfoRow: React.ReactNode = (
		<div className="submission-confirm-user-info-card-container">
			<CollapsibleCard
				className="card-small-padding"
				expanded={testReportInfoCardExpanded}
				content={
					<>
						<div
							className="collapsible-icon-card-header"
							onClick={() => {
								setTestReportInfoCardExpanded(!testReportInfoCardExpanded);
							}}
						>
							<UserIcon />
							<div className="typography-heading-03">
								{translation("TestReportInformation")}
							</div>

							<div
								className="typography-caption icon-card-header-description "
								style={{ color: NeutralColor.NEUTRAL_GREY_500 }}
							>
								{translation("TestReportInformationDescription")}
							</div>
							{testReportInfoCardExpanded ? (
								<RemoveIcon
									style={{ color: NeutralColor.NEUTRAL_DARK, fontSize: 24 }}
								/>
							) : (
								<AddIcon
									style={{ color: NeutralColor.NEUTRAL_DARK, fontSize: 24 }}
								/>
							)}
						</div>
						<Divider orientation="horizontal" flexItem sx={{ my: 1 }}></Divider>
					</>
				}
				collapsibleContent={
					<>
						<div className="submission-confirm-user-info-card-content">
							<div className="test-report-info-content-grid">
								<div className="test-report-info-content-container">
									<div className="submission-confirm-user-info-row typography-body-02">
										<div className="submission-confirm-info-row-title">
											{translation("ClientName")}
										</div>
										<div className="submission-confirm-info-row-info">
											{props.order
												? props.order.orderedBy
												: `${selectedClient?.firstName} ${selectedClient?.lastName}`}
										</div>
									</div>
									<div className="submission-confirm-user-info-row typography-body-02">
										<div className="submission-confirm-info-row-title">
											{translation("Email")}
										</div>
										<div className="submission-confirm-info-row-info">
											{props.order ? props.order.email : selectedClient?.email}
										</div>
									</div>
									<div className="submission-confirm-user-info-row typography-body-02">
										<div className="submission-confirm-info-row-title">
											{translation("PhoneNumber")}
										</div>
										<div className="submission-confirm-info-row-info">
											{props.order
												? props.order.phoneNumber
												: selectedClient?.phoneNumber}
										</div>
									</div>
								</div>

								<Divider
									className="test-report-info-content-grid-divider"
									orientation="horizontal"
									flexItem
									sx={{ my: 1 }}
								></Divider>
								<div className="test-report-info-content-container">
									<div className="submission-confirm-user-info-row typography-body-02">
										<div className="submission-confirm-info-row-title">
											{translation("CompanyName")}
										</div>
										<div className="submission-confirm-info-row-info">
											{props.order
												? props.order.clientCompanyName
												: selectedOrganization?.clientCompanyName}
										</div>
									</div>
									<div className="submission-confirm-user-info-row typography-body-02">
										<div className="submission-confirm-info-row-title">
											{translation("Address")}
										</div>
										<div className="submission-confirm-info-row-info">
											<div>{`${
												props.order
													? props.order.address
													: selectedOrganization?.address
											}, `}</div>
											<div>{`${
												props.order
													? props.order.city
													: selectedOrganization?.city
											}, ${
												props.order
													? props.order.province
													: selectedOrganization?.province
											}, ${
												props.order
													? props.order.country
													: selectedOrganization?.country
											} `}</div>
											<div>
												{props.order
													? props.order.postalCode
													: selectedOrganization?.postalCode}
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
						<Divider
							orientation="horizontal"
							flexItem
							sx={{ margin: "20px 0 0 0" }}
						></Divider>
					</>
				}
				moreContent={
					<div className="submission-confirm-user-info-card-content">
						<CopyEmailSection
							title={translation("SendTestReportCopiesTo")}
							button={
								<TextButton
									id={"add-test-report-email-button"}
									onClick={() => {
										newTestReportEmail();
									}}
									size="xsmall"
								>
									<AddIcon style={{ fontSize: 20 }} />
									<span className="typography-body-02">
										{translation("Email")}
									</span>
								</TextButton>
							}
							items={testReportEmailItems}
						></CopyEmailSection>
					</div>
				}
			></CollapsibleCard>
			<CollapsibleCard
				className="card-small-padding"
				expanded={billingInfoInfoCardExpanded}
				content={
					<>
						<div
							className="collapsible-icon-card-header"
							onClick={() => {
								setBillingInfoInfoCardExpanded(!billingInfoInfoCardExpanded);
							}}
						>
							<BillingIcon />
							<div className="typography-heading-03">
								{translation("BillingInformation")}
							</div>

							<div
								className="typography-caption icon-card-header-description "
								style={{ color: NeutralColor.NEUTRAL_GREY_500 }}
							>
								{translation("BillingInformationDescription")}
							</div>
							{billingInfoInfoCardExpanded ? (
								<RemoveIcon
									style={{ color: NeutralColor.NEUTRAL_DARK, fontSize: 24 }}
								/>
							) : (
								<AddIcon
									style={{ color: NeutralColor.NEUTRAL_DARK, fontSize: 24 }}
								/>
							)}
						</div>
						<Divider orientation="horizontal" flexItem sx={{ my: 1 }}></Divider>
					</>
				}
				collapsibleContent={
					<>
						<div className="submission-confirm-user-info-card-content">
							<div className="billing-info-same-info-switch">
								<div className="typography-body-01">
									{translation("SameAsTestReportInformation")}
								</div>
								<CommonSwitch
									id="same-billing-info-switch"
									checked={sameBillingInfoAsTestInfo}
									setChecked={setSameBillingInfoAsTestInfo}
								/>
							</div>
							<div
								style={{
									display: sameBillingInfoAsTestInfo ? "none" : "flex",
									flexDirection: "column",
									gap: "5px",
									marginTop: "10px",
								}}
							>
								<div className="billing-info-details-grid-row">
									{billingInputs[0]}
									{billingInputs[1]}
								</div>
								<div className="billing-info-details-grid-row">
									{billingInputs[2]}
									{billingInputs[3]}
									{billingInputs[4]}
									{billingInputs[5]}
								</div>
								<div className="billing-info-details-grid-row">
									{billingInputs[6]}
									{billingInputs[7]}
								</div>
							</div>
						</div>
						<Divider
							orientation="horizontal"
							flexItem
							sx={{ margin: "20px 0 0 0" }}
						></Divider>
					</>
				}
				moreContent={
					<div className="submission-confirm-user-info-card-content">
						<CopyEmailSection
							title={translation("BillingInformationCopiesTo")}
							button={
								<TextButton
									id={"add-test-report-email-button"}
									onClick={() => {
										newBillingInfoEmail();
									}}
									size="xsmall"
								>
									<AddIcon style={{ fontSize: 20 }} />
									<span className="typography-body-02">
										{translation("Email")}
									</span>
								</TextButton>
							}
							items={billingInfoEmailItems}
						></CopyEmailSection>
					</div>
				}
			></CollapsibleCard>
			{orderCommentsComponent}
			<CollapsibleCard
				className="card-small-padding"
				expanded={sampleInfoCardExpanded}
				content={
					<>
						<div
							className="collapsible-icon-card-header"
							onClick={() => {
								setSampleInfoCardExpanded(!sampleInfoCardExpanded);
							}}
						>
							<OrderIcon strokeColor={PrimaryColor.PRIMARY_LV5} />
							<div className="typography-heading-03">
								{translation("SamplesAndTests")}
							</div>
							{sampleInfoCardExpanded ? (
								<RemoveIcon
									style={{ color: NeutralColor.NEUTRAL_DARK, fontSize: 24 }}
								/>
							) : (
								<AddIcon
									style={{ color: NeutralColor.NEUTRAL_DARK, fontSize: 24 }}
								/>
							)}
						</div>
						<Divider orientation="horizontal" flexItem sx={{ my: 1 }}></Divider>
					</>
				}
				collapsibleContent={
					<SamplePreviewTable
						company={company}
						setCompany={setCompany}
						user={user}
						sampleInstances={sampleInstances}
						orderTestCategory={orderTestCategory}
						isReadOnlyTable={true}
						goToUpdateSample={() => {}}
						deleteSample={() => {}}
						duplicateSample={() => {}}
						isInNestedCard={true}
						openEditExpectationModal={() => {}}
						specDirectories={specDirectories}
						updateSpecification={updateSpecification}
					/>
				}
			></CollapsibleCard>
		</div>
	);

	return (
		<div style={{ margin: "0 auto" }}>
			{headerRow}
			{userInfoRow}
		</div>
	);
}
